/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
/* eslint-disable react/require-default-props */
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import Template from 'react-day-picker';
import { Context } from '@front10/helpers/dist/Context';
import DatePickerCaption from '../DatePicker/DatePickerCaption';
import DatePickerDay from '../DatePicker/DatePickerDay';
import DatePickerNav from '../DatePicker/DatePickerNav';
import DatePickerWeekday from '../DatePicker/DatePickerWeekday';
import Box from '../../elements/Box';
import { fullMonths } from '../../../helpers/localization/localizationUtils';
import { formatDate, parseDate } from '../../../helpers/date';

function DayPicker({
  forwardedRef,
  format,
  selectedDays,
  modifiers,
  modifiersColors,
  weekdayElement,
  weekdayItem,
  currentMonth,
  dayItems,
  renderDay: dayElement,
  showInOneColumn,
  navColor,
  navigationItem,
  navbarElement,
  onFocus,
  onBlur,
  onDayClick,
  onDayMouseEnter,
  onDayMouseLeave,
  onMonthChange,
  firstDayOfWeek,
  availableDays,
  captionElement,
  captionItem,
  numberOfMonths,
  departure,
  ...props
}) {
  const { translate, direction, localizationData } = useContext(Context);
  const today = dayjs().toDate();
  const months = fullMonths(translate, localizationData);
  const toMonth =
    availableDays && Number(availableDays) && availableDays > 0
      ? dayjs()
          .add(availableDays - 1, 'day')
          .toDate()
      : null;
  const disabledDays = {
    before:
      departure && departure.start && Number(departure.start) && Number(departure.start) >= 0
        ? dayjs().add(Number(departure.start), 'day').toDate()
        : today
  };
  if (toMonth && dayjs(toMonth).isValid()) {
    disabledDays.after = toMonth;
  }
  const dow =
    localizationData && localizationData._week && localizationData._week.dow
      ? localizationData._week.dow
      : firstDayOfWeek;

  function focus() {
    if (forwardRef.current) {
      forwardRef.current.focus();
    }
  }

  function renderWeekdayElement({ weekday, localizationUtils, locale }) {
    if (weekdayElement && typeof weekdayElement === 'function') {
      return weekdayElement({ weekday, localizationUtils, locale });
    }
    return <DatePickerWeekday {...weekdayItem} weekday={weekday} />;
  }

  function renderCaptionElement({ date, localeUtils, locale }) {
    if (captionElement && typeof captionElement === 'function') {
      return captionElement({ date, localeUtils, locale });
    }
    return (
      <DatePickerCaption
        {...captionItem}
        date={date}
        localizationUtils={localeUtils}
        locale={locale}
      />
    );
  }

  function renderDay(day) {
    const colors = modifiersColors || {};
    if (dayElement && typeof dayElement === 'function') {
      return dayElement(day);
    }
    let dayProps = Object.assign({}, dayItems);

    Object.keys(colors).every((i) => {
      if (modifiers[i] && typeof modifiers[i] === 'function' && modifiers[i](day)) {
        dayProps = Object.assign(dayProps, colors[i]);
        return false;
      }
      return true;
    });
    return <DatePickerDay border={false} day={day} {...dayProps} />;
  }

  const renderNavbarElement = ({
    nextMonth,
    previousMonth,
    onPreviousClick,
    onNextClick,
    localizationUtils
  }) => {
    if (navbarElement && typeof navbarElement === 'function') {
      return navbarElement({
        nextMonth,
        previousMonth,
        onPreviousClick,
        onNextClick,
        localizationUtils
      });
    }
    return (
      <DatePickerNav
        {...navigationItem}
        nextMonth={nextMonth}
        previousMonth={previousMonth}
        onPreviousClick={() => {
          focus();
          onPreviousClick();
        }}
        onNextClick={() => {
          focus();
          onNextClick();
        }}
        localizationUtils={localizationUtils}
        color={navColor}
        iconPrevious="left-chevron"
        iconNext="right-chevron"
        tabIndex={-1}
        toMonth={toMonth}
      />
    );
  };

  return (
    <Box
      direction={direction}
      className="fr-date-selector DayPickerInput-OverlayWrapper"
      {...props}
    >
      <Box
        className={`${showInOneColumn ? 'DayPickerInput-Overlay-OneColumn fr-shadow-none' : ''}`}
      >
        <Template
          ref={forwardedRef}
          format={format}
          formatDate={formatDate}
          parseDate={parseDate}
          selectedDays={selectedDays}
          disabledDays={disabledDays}
          modifiers={modifiers}
          numberOfMonths={numberOfMonths}
          fromMonth={today}
          initialMonth={currentMonth}
          months={months}
          localizationUtils={localizationData}
          weekdayElement={renderWeekdayElement}
          renderDay={renderDay}
          modifiersStyles={modifiersColors}
          captionElement={renderCaptionElement}
          navbarElement={renderNavbarElement}
          onDayMouseEnter={onDayMouseEnter}
          onDayMouseLeave={onDayMouseLeave}
          onDayClick={onDayClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onMonthChange={onMonthChange}
          tabIndex={-1}
          firstDayOfWeek={dow}
          toMonth={toMonth}
        />
      </Box>
    </Box>
  );
}

DayPicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.objectOf(PropTypes.any),
  placeholder: PropTypes.string,
  format: PropTypes.string,
  selectedDays: PropTypes.arrayOf(PropTypes.any),
  modifiers: PropTypes.objectOf(PropTypes.any),
  modifiersColors: PropTypes.objectOf(PropTypes.any),
  captionElement: PropTypes.func,
  weekdayElement: PropTypes.objectOf(PropTypes.any),
  weekdayItem: PropTypes.objectOf(PropTypes.any),
  dayItems: PropTypes.objectOf(PropTypes.any),
  renderDay: PropTypes.func,
  direction: PropTypes.string,
  showInOneColumn: PropTypes.bool,
  navColor: PropTypes.string,
  navigationItem: PropTypes.objectOf(PropTypes.any),
  navbarElement: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onDayClick: PropTypes.func,
  onDayMouseEnter: PropTypes.func,
  onDayMouseLeave: PropTypes.func,
  onMonthChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  currentMonth: PropTypes.objectOf(PropTypes.any),
  /**
   * First Day Of the Week
   */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  availableDays: PropTypes.number,
  numberOfMonths: PropTypes.number
};

DayPicker.defaultProps = {
  firstDayOfWeek: 0,
  availableDays: null,
  captionElement: null
};

// eslint-disable-next-line react/display-name
export default forwardRef((props, ref) => <DayPicker forwardedRef={ref} {...props} />);
